import { AutocompleteRenderOptionState } from '@mui/material';
import { HTMLAttributes, Key, Ref, SyntheticEvent, forwardRef } from 'react';
import { isScrolledToEnd } from '../../../../../utils/generics';
import Autocomplete from '../../Autocomplete/Autocomplete';
import { AsyncAutocompleteValue, AsyncAutocompleteViewProps } from '../asyncAutocomplete.types';
import { useListboxScrollAnchor } from '../hooks/useListboxScrollAnchor';
import AsyncAutocompleteOption from './AsyncAutocompleteOption';

const SCROLL_END_THRESHOLD_PX = 8;

const keepAllOptions = <O,>(options: O[]) => options;

function AsyncAutocompleteView<Multiple extends boolean | undefined = undefined>(
  {
    options,
    listKey,
    value,
    onChange,
    getOptionLabel,
    renderOption,
    loading,
    isResolvingValue,
    onSearch,
    onScrollEnd,
    onOpen,
    createOptionId,
    multiple,
    open,
    onClose,
    label,
    placeholder,
    required,
    error,
    helperText,
    name,
    onBlur,
    getChipConfig,
    dropdownMinWidth,
    ...presentation
  }: AsyncAutocompleteViewProps<Multiple>,
  ref: Ref<HTMLInputElement>,
) {
  const { trackListboxScroll } = useListboxScrollAnchor({
    optionCount: options.length,
    listKey,
    isLoading: !!loading,
  });

  const handleScroll = (event: SyntheticEvent) => {
    const node = event.currentTarget as HTMLElement;
    const isUserScroll = trackListboxScroll(node);
    if (isUserScroll && isScrolledToEnd(node, SCROLL_END_THRESHOLD_PX)) {
      onScrollEnd();
    }
  };

  const renderOptionAdapter =
    renderOption || createOptionId
      ? (
          props: HTMLAttributes<HTMLLIElement> & { key?: Key },
          option: string,
          state: AutocompleteRenderOptionState,
        ) => {
          const { key: _labelKey, ...optionProps } = props;

          return (
            <AsyncAutocompleteOption
              key={option}
              props={optionProps}
              option={option}
              state={state}
              multiple={multiple}
              isCreateOption={option === createOptionId}
              getOptionLabel={getOptionLabel}
              renderOption={renderOption}
            />
          );
        }
      : undefined;

  return (
    <Autocomplete<string, Multiple, boolean>
      ref={ref}
      {...presentation}
      open={open}
      multiple={multiple}
      getChipConfig={getChipConfig}
      options={options}
      value={value}
      loading={loading || isResolvingValue}
      disableCloseOnSelect={multiple}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(a, b) => a === b}
      filterOptions={keepAllOptions}
      renderOption={renderOptionAdapter}
      onBlur={onBlur}
      onClose={onClose}
      onOpen={onOpen}
      slotProps={{
        listbox: { onScroll: handleScroll },
        popper: dropdownMinWidth
          ? { placement: 'bottom-end', style: { minWidth: dropdownMinWidth } }
          : undefined,
      }}
      textFieldProps={{ label, placeholder, required, error, helperText, name }}
      onChange={(_event, nextValue) => onChange(nextValue as AsyncAutocompleteValue<Multiple>)}
      onInputChange={onSearch}
    />
  );
}

const ForwardedAsyncAutocompleteView = forwardRef(AsyncAutocompleteView);
ForwardedAsyncAutocompleteView.displayName = 'AsyncAutocompleteView';

export default ForwardedAsyncAutocompleteView as <Multiple extends boolean | undefined = undefined>(
  props: AsyncAutocompleteViewProps<Multiple> & { ref?: Ref<HTMLInputElement> },
) => React.ReactElement;
